import {useEffect, useState} from 'react'

import {type NetworkHints} from './mediaPolicy'

/**
 * Thin access to the Network Information API (effectiveType / downlink /
 * saveData) as the policy hints for the media preload window. Treated as
 * hints only — mediaPolicy clamps them into a coarse profile. Safe on every
 * platform: absent API → all-null hints (the policy assumes 'fast' and keeps
 * speculative work bounded to one segment).
 */

type ConnLike = {
  effectiveType?: string
  downlink?: number
  saveData?: boolean
  addEventListener?: (type: string, cb: () => void) => void
  removeEventListener?: (type: string, cb: () => void) => void
}

function readHints(): NetworkHints {
  if (typeof navigator === 'undefined') {
    return {downlink: null, effectiveType: null, saveData: false}
  }
  const conn = (navigator as {connection?: ConnLike}).connection
  if (!conn) return {downlink: null, effectiveType: null, saveData: false}
  return {
    downlink:
      typeof conn.downlink === 'number' && Number.isFinite(conn.downlink)
        ? conn.downlink
        : null,
    effectiveType: conn.effectiveType ?? null,
    saveData: !!conn.saveData,
  }
}

/** Read the current hints imperatively (test/emulation entry point). */
export function getNetworkHints(): NetworkHints {
  return readHints()
}

/** Subscribe to connection changes; re-reads on every 'change' event. */
export function useNetworkHints(): NetworkHints {
  const [hints, setHints] = useState<NetworkHints>(readHints)
  useEffect(() => {
    const conn = (navigator as {connection?: ConnLike}).connection
    if (!conn?.addEventListener) return
    const onChange = () => setHints(readHints())
    conn.addEventListener('change', onChange)
    return () => conn.removeEventListener?.('change', onChange)
  }, [])
  return hints
}
